import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
    ActiveFilterChip,
    CommentCounts,
    CommentFilterKey,
    NoteKind,
    NoteRow,
    NoteSort,
    NoteSortDirection,
    NoteSortKey,
    TodoAudienceKey,
    TodoCounts,
    TodoStatusKey,
} from './notes-overview.types';
import { ariaSortOf, directionOf, nextSort } from '../sort-header/sort-header.types';

//REV-21372 — Die beiden Uebersichten (Pure View). Kein Store, kein HTTP, KEINE Sortierung, keine
//Filterung, kein Rechnen von Datum oder Zahl. Entwurf: ADR-0019 E7.
//
//⚠️ Die Komponente sortiert und filtert NICHT. Sie zeigt `rows`, wie sie hereinkommen, und meldet,
//was der Nutzer angeklickt hat. Der Regler sitzt im Host — sonst gaeben Tabelle und CSV-Export
//verschiedene Antworten auf dieselbe Frage.

/** Spaltensatz je Art (ADR-0019 E7). Die Reihenfolge ist die des Prototyps. */
const COLUMNS_BY_KIND: Record<NoteKind, NoteSortKey[]> = {
    COMMENT: ['text', 'tags', 'account', 'author', 'created'],
    TODO: ['text', 'tags', 'account', 'created', 'due'],
};

/**
 * Spaltentitel je Art — dieselbe Spalte heisst anders.
 *
 * ⚠️ `tags` ist am Kommentar die **Klassifizierung**, an der Pendenz die **Zielgruppe**. Ein
 * gemeinsamer Titel waere an einer der beiden Stellen falsch.
 */
const COLUMN_LABEL_KEYS: Record<NoteKind, Record<NoteSortKey, string>> = {
    COMMENT: {
        text: 'aumico_abcp_notes_col_comment',
        tags: 'aumico_abcp_notes_col_classification',
        account: 'aumico_abcp_notes_col_account',
        author: 'aumico_abcp_notes_col_author',
        created: 'aumico_abcp_notes_col_created',
        due: 'aumico_abcp_notes_col_due',
    },
    TODO: {
        text: 'aumico_abcp_notes_col_todo',
        tags: 'aumico_abcp_notes_col_audience',
        account: 'aumico_abcp_notes_col_account',
        author: 'aumico_abcp_notes_col_author',
        created: 'aumico_abcp_notes_col_created',
        due: 'aumico_abcp_notes_col_due',
    },
};

const COMMENT_FILTERS: CommentFilterKey[] = ['all', 'internal', 'closingMeeting'];
const TODO_STATUS: TodoStatusKey[] = ['open', 'done'];
const TODO_AUDIENCES: TodoAudienceKey[] = ['all', 'todo', 'reviewer', 'client'];

const COMMENT_FILTER_LABEL_KEYS: Record<CommentFilterKey, string> = {
    all: 'aumico_abcp_notes_filter_all',
    internal: 'aumico_abcp_notes_tag_internal',
    closingMeeting: 'aumico_abcp_notes_tag_closingmeeting',
};

const TODO_STATUS_LABEL_KEYS: Record<TodoStatusKey, string> = {
    open: 'aumico_abcp_notes_filter_open',
    done: 'aumico_abcp_notes_filter_done',
};

/**
 * ⚠️ Die Zielgruppen-Pille heisst im Register **Sachbearbeiter**, im Verfasser am Konto aber
 * **To-Do** — derselbe Wert, zwei Beschriftungen, so hat es der Prototyp. Deshalb ein eigener
 * Schluesselsatz statt `aumico_abcp_notes_tag_*`: eine gemeinsame Beschriftung waere an einer der
 * beiden Stellen falsch.
 */
const TODO_AUDIENCE_LABEL_KEYS: Record<TodoAudienceKey, string> = {
    all: 'aumico_abcp_notes_filter_all',
    todo: 'aumico_abcp_notes_audience_todo',
    reviewer: 'aumico_abcp_notes_audience_reviewer',
    client: 'aumico_abcp_notes_audience_client',
};

@Component({
    selector: 'abcp-notes-overview',
    templateUrl: './notes-overview.component.html',
    styleUrls: ['./notes-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpNotesOverviewComponent {
    /**
     * Welche der beiden Uebersichten dies ist. Entscheidet Spalten, Pillen, Leertext und
     * Vorspalte — und **nichts sonst** (ADR-0019 E7).
     */
    @Input() kind: NoteKind = 'COMMENT';

    /** Die Zeilen, **fertig sortiert und gefiltert**. Die Ansicht ordnet nicht um. */
    @Input() rows: NoteRow[] = [];

    // ⚠️ KEIN `totalCount` mehr. Das Register traegt keinen eigenen Titel — wie das Nachbar-
    // Register auch nicht —, und die Gesamtzahl steht in der Pille des Reiters darueber
    // (`ClosingPage.tabCount`). Zwei Zahlen fuer dieselbe Menge driften auseinander, und die
    // zweite ist dann die, der niemand glaubt.

    @Input() commentFilter: CommentFilterKey = 'all';
    @Input() commentCounts: CommentCounts = { all: 0, internal: 0, closingMeeting: 0 };

    @Input() todoStatus: TodoStatusKey = 'open';
    @Input() todoAudience: TodoAudienceKey = 'all';
    @Input() todoCounts: TodoCounts = { open: 0, done: 0, todo: 0, reviewer: 0, client: 0 };

    /** `null` ⇒ die Vorsortierung des Hosts, ohne Pfeil an einer Spalte. */
    @Input() sort: NoteSort | null = null;

    /** Was gerade gilt, fertig beschriftet — einzeln abwerfbar (AC4). */
    @Input() activeFilters: ActiveFilterChip[] = [];

    @Input() searchTerm = '';

    /** `null` ⇒ es wurde nicht gesucht. `0` ist ein Ergebnis, keine Abwesenheit. */
    @Input() searchHits: number | null = null;

    /**
     * Die Zeile, die gerade abgehakt/wieder geoeffnet wurde (AC8).
     *
     * ⚠️ Kontrolliert. Sie bleibt sichtbar, bis der Host sie wegnimmt — eine Zeile, die sich selbst
     * einen Timer stellt, entscheidet ueber die Sichtbarkeit von Daten, die ihr nicht gehoeren.
     */
    @Input() recentlyChangedId: string | null = null;

    @Input() loading = false;
    @Input() error = false;

    @Output() commentFilterChange = new EventEmitter<CommentFilterKey>();
    @Output() todoStatusChange = new EventEmitter<TodoStatusKey>();
    @Output() todoAudienceChange = new EventEmitter<TodoAudienceKey>();
    /** `null` = zurueck zur Vorsortierung (Kommentare neueste zuerst, Pendenzen nach Faelligkeit). */
    @Output() sortChange = new EventEmitter<NoteSort | null>();
    @Output() filterRemove = new EventEmitter<NoteSortKey>();
    @Output() filtersReset = new EventEmitter<void>();
    @Output() searchReset = new EventEmitter<void>();
    @Output() rowOpen = new EventEmitter<string>();
    @Output() todoDoneToggle = new EventEmitter<string>();
    // ⚠️ KEIN `undoRequested` mehr: die Ruecknahme sitzt im Toast, den der Host ausloest — mit
    // „Rueckgaengig" direkt in der Meldung, wie im Prototyp. Eine Ansicht, die sie zusaetzlich
    // anboete, haette zwei Knoepfe fuer dieselbe Handlung.
    /** ⚠️ NUR im Pendenzen-Register (AC11): eine Pendenz entsteht hier, ein Kommentar am Konto. */
    @Output() createRequested = new EventEmitter<void>();
    @Output() exportRequested = new EventEmitter<void>();
    /** „Druckansicht" steht im Menue, ist aber bewusst NICHT gebaut (ADR-0019 E8). */
    @Output() printRequested = new EventEmitter<void>();
    @Output() retryRequested = new EventEmitter<void>();

    readonly skeletonRows = [1, 2, 3, 4, 5];

    /** Das Export-Menue ist ansichts-lokal: ein offenes Menue ist kein Zustand des Abschlusses. */
    exportMenuOpen = false;

    toggleExportMenu(): void {
        this.exportMenuOpen = !this.exportMenuOpen;
    }

    onExport(): void {
        this.exportMenuOpen = false;
        this.exportRequested.emit();
    }

    /**
     * ⚠️ Nur das PENDENZEN-Register traegt den Anlegen-Knopf (AC11). Ein Kommentar entsteht am
     * Konto, weil er dorthin gehoert — ein zweiter Einstieg hier wuerde ihn ohne Kontobezug
     * erzeugen, und genau das soll es nicht geben.
     */
    get mayCreate(): boolean {
        return this.isTodo;
    }

    // ── Art ───────────────────────────────────────────────────────────────────────────────────

    get isTodo(): boolean {
        return this.kind === 'TODO';
    }

    get columns(): NoteSortKey[] {
        return COLUMNS_BY_KIND[this.kind];
    }

    columnLabelKey(column: NoteSortKey): string {
        return COLUMN_LABEL_KEYS[this.kind][column];
    }

    // ── Pillen ────────────────────────────────────────────────────────────────────────────────

    readonly commentFilters = COMMENT_FILTERS;
    readonly todoStatuses = TODO_STATUS;
    readonly todoAudiences = TODO_AUDIENCES;

    commentFilterLabelKey(key: CommentFilterKey): string {
        return COMMENT_FILTER_LABEL_KEYS[key];
    }

    todoStatusLabelKey(key: TodoStatusKey): string {
        return TODO_STATUS_LABEL_KEYS[key];
    }

    todoAudienceLabelKey(key: TodoAudienceKey): string {
        return TODO_AUDIENCE_LABEL_KEYS[key];
    }

    commentCount(key: CommentFilterKey): number {
        return this.commentCounts[key];
    }

    todoStatusCount(key: TodoStatusKey): number {
        return this.todoCounts[key];
    }

    todoAudienceCount(key: TodoAudienceKey): number {
        return key === 'all' ? this.todoCounts[this.todoStatus] : this.todoCounts[key];
    }

    // ── Sortierung ────────────────────────────────────────────────────────────────────────────

    sortDirection(column: NoteSortKey): NoteSortDirection | null {
        return directionOf(this.sort, column);
    }

    ariaSort(column: NoteSortKey): 'ascending' | 'descending' | 'none' {
        return ariaSortOf(this.sort, column);
    }

    /** Neue Spalte aufsteigend, dieselbe absteigend, ein dritter Klick hebt die Sortierung auf. */
    onSort(column: NoteSortKey): void {
        this.sortChange.emit(nextSort(this.sort, column));
    }

    // ── Zeilen ────────────────────────────────────────────────────────────────────────────────

    trackById(_index: number, row: NoteRow): string {
        return row.id;
    }

    /**
     * ⚠️ Eine erledigte Pendenz ist nie ueberfaellig (AC6). Die Regel steht beim Host; hier wird sie
     * ein zweites Mal durchgesetzt, weil ein durchgestrichener roter Balken eine Aussage waere, die
     * niemand treffen wollte.
     */
    isOverdue(row: NoteRow): boolean {
        return !!row.overdue && !row.done;
    }

    isRecentlyChanged(row: NoteRow): boolean {
        return this.recentlyChangedId === row.id;
    }

    onRowOpen(row: NoteRow): void {
        this.rowOpen.emit(row.id);
    }

    /** Das Kaestchen ist kein Zeilen-Klick: es hakt ab, es oeffnet nicht. */
    onDoneToggle(row: NoteRow, event: Event): void {
        event.stopPropagation();
        this.todoDoneToggle.emit(row.id);
    }

    // ── Zustaende ─────────────────────────────────────────────────────────────────────────────

    /**
     * ⚠️ „Hier ist nichts" und „hier ist etwas, nur nicht das Gesuchte" sind VERSCHIEDENE Saetze
     * (AC12) — und nur der zweite hat einen Ausweg. Ein leerer Abschluss ist kein Fehlschlag der
     * Suche, und ihm einen „Filter zuruecksetzen"-Knopf anzubieten, waere eine Sackgasse.
     */
    get isEmpty(): boolean {
        return this.rows.length === 0 && !this.hasNarrowing;
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.rows.length === 0;
    }

    get isNoMatch(): boolean {
        return this.rows.length === 0 && this.hasNarrowing;
    }

    get hasNarrowing(): boolean {
        return this.activeFilters.length > 0 || this.searchTerm.trim().length > 0;
    }
}
